import type { Texture } from 'three';

const STALL_RETRY_MS = 8000;

export type StallRetryLoadFn = (url: string, onLoad: (texture: Texture) => void, onError: (err: unknown) => void) => void;

export type LoadWithStallRetryParams = {
	url: string;
	label: string;
	load: StallRetryLoadFn;
	onLoad: (texture: Texture) => void;
	onError: (err: unknown) => void;
};

/**
 * Guards a texture load against Three's shared FileLoader in-flight dedup stranding it
 * (a piggybacked fetch whose earlier callback threw never invokes later callbacks).
 * If the load has not settled within STALL_RETRY_MS, a cache-busted URL forces a fresh
 * fetch that bypasses both the dedup map and the corrupted entry.
 */
export const loadWithStallRetry = (params: LoadWithStallRetryParams): void => {
	const { url, label, load, onLoad, onError } = params;
	let settled = false;
	let timer: ReturnType<typeof setTimeout> | undefined;
	const settle = (fn: () => void): void => {
		if (settled) return;
		settled = true;
		clearTimeout(timer);
		fn();
	};
	timer = setTimeout(() => {
		if (settled) return;
		console.warn(`[TextureManager] texture stall >${STALL_RETRY_MS}ms: ${label} ${url} — retrying with cache-bust`);
		const busted = `${url}${url.includes('?') ? '&' : '?'}stallRetry=${Date.now()}`;
		load(busted, (texture) => settle(() => onLoad(texture)), (err) => settle(() => onError(err)));
	}, STALL_RETRY_MS);
	load(url, (texture) => settle(() => onLoad(texture)), (err) => settle(() => onError(err)));
};
